import { Dialog, serviceCall } from "@serenity-is/corelib";
import { AskManualRequest, AskManualResponse, AskManualSection, ManualService } from "../../ServerTypes/Common";
import { ManualTexts } from "../../ServerTypes/Texts";

/**
 * The Ask the manual button in the top bar (_Sidebar.cshtml renders it only while the feature is on) and the
 * panel it opens: a question box, then the answer, its steps and links to the screens the manual sections are
 * about. Every link comes from the app's own sections; the model's words are plain text, never markup.
 */
export function initAskManual() {
    if (typeof document === "undefined" || (document as any).askManualReady)
        return;
    (document as any).askManualReady = true;

    const start = () => document.querySelectorAll<HTMLElement>(".s-ask-manual-link").forEach(link =>
        link.addEventListener("click", e => {
            e.preventDefault();
            openAskManual();
        }));

    if (document.body)
        start();
    else
        document.addEventListener("DOMContentLoaded", start, { once: true });
}

export function openAskManual() {
    const question = <textarea class="form-control ask-manual-question" rows={2} maxlength={300}
        placeholder={ManualTexts.Placeholder} aria-label={ManualTexts.Button} /> as HTMLTextAreaElement;
    const button = <button type="submit" class="btn btn-primary ask-manual-ask">
        <i class="fa fa-search"></i> {ManualTexts.Ask}
    </button> as HTMLButtonElement;
    const result = <div class="ask-manual-result" aria-live="polite"></div> as HTMLElement;

    const ask = () => {
        const text = question.value.trim();
        if (!text || button.disabled)
            return;
        button.disabled = true;
        result.replaceChildren(<p class="text-muted"><i class="fa fa-spinner fa-spin"></i> {ManualTexts.Asking}</p> as HTMLElement);
        serviceCall<AskManualResponse>({
            service: ManualService.Methods.Ask,
            request: { Question: text } satisfies AskManualRequest,
            onSuccess: response => result.replaceChildren(...answer(response)),
            onError: () => result.replaceChildren(),
            onCleanup: () => { button.disabled = false; }
        });
    };

    const content = <div class="ask-manual">
        <style>{`
            .ask-manual form { display: flex; gap: .5rem; align-items: flex-start; }
            .ask-manual form textarea { flex: 1; resize: vertical; }
            .ask-manual-result { margin-top: 1rem; }
            .ask-manual-result ol { padding-inline-start: 1.5rem; }
            .ask-manual-result ol li { margin-bottom: .25rem; }
            .ask-manual-sections { list-style: none; padding: 0; margin: 0; }
            .ask-manual-sections li { padding: .25rem 0; }
            .ask-manual-sections .ask-manual-menu { color: var(--bs-secondary-color, #6c757d); }
            .ask-manual-note { margin: 1rem 0 0; font-size: .85em; color: var(--bs-secondary-color, #6c757d); }
        `}</style>
        <form onSubmit={(e: Event) => { e.preventDefault(); ask(); }}>
            {question}
            {button}
        </form>
        {result}
        <p class="ask-manual-note">{ManualTexts.Disclaimer}</p>
    </div> as HTMLElement;

    question.addEventListener("keydown", e => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            ask();
        }
    });

    new Dialog({
        title: ManualTexts.Button,
        dialogClass: "s-AskManualDialog",
        size: "lg",
        element: el => el.append(content),
        onOpen: () => question.focus()
    });
}

function answer(response: AskManualResponse): HTMLElement[] {
    const sections = response.Sections ?? [];
    if (!response.Found)
        return [<p>{ManualTexts.NothingFound}</p> as HTMLElement];
    if (!response.Covered)
        return [<p>{ManualTexts.NotCovered}</p> as HTMLElement, sectionList(sections)];

    const parts: HTMLElement[] = [];
    if (response.Answer)
        parts.push(<p class="ask-manual-answer">{response.Answer}</p> as HTMLElement);
    if (response.Steps?.length)
        parts.push(<ol class="ask-manual-steps">{response.Steps.map(step => <li>{step}</li>)}</ol> as HTMLElement);
    if (sections.length)
        parts.push(<h6 class="mt-3">{ManualTexts.FromTheManual}</h6> as HTMLElement, sectionList(sections));
    return parts;
}

function sectionList(sections: AskManualSection[]) {
    return <ul class="ask-manual-sections">
        {sections.map(s => <li>
            {s.Url
                ? <a href={s.Url}><i class="fa fa-external-link-square"></i> {s.Menu}</a>
                : <span>{s.Title}</span>}
            {s.Url && <span class="ask-manual-menu"> · {s.Title}</span>}
        </li>)}
    </ul> as HTMLElement;
}
